/** Validadores puros para formularios de autenticación. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const E164_RE = /^\+[1-9]\d{7,14}$/;

/** Quita espacios, guiones, puntos y paréntesis de un teléfono. */
export function normalizePhoneInput(value: string): string {
  return value.replace(/[\s\-().]/g, '');
}

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/** Países cuyo número nacional tiene exactamente 10 dígitos (México y NANP/EE. UU.). */
const TEN_DIGIT_DIALS = ['52', '1'];

/** Solo dígitos del número nacional, sin un 0 inicial. */
export function normalizeNationalInput(value: string): string {
  return value.replace(/\D/g, '').replace(/^0/, '');
}

/**
 * ¿Es válido el número nacional para el código de marcación (sin '+')?
 * México/EE. UU.: exactamente 10 dígitos. Otros: E.164 total de 8 a 15 dígitos.
 */
export function isValidNationalPhone(dial: string, national: string): boolean {
  if (!/^\d+$/.test(national)) return false;
  if (TEN_DIGIT_DIALS.includes(dial)) return national.length === 10;
  const total = dial.length + national.length;
  return total >= 8 && total <= 15;
}

/** Teléfono E.164 (tras normalizar); México/EE. UU. exigen 10 dígitos nacionales. */
export function isValidPhoneE164(value: string): boolean {
  const v = normalizePhoneInput(value);
  if (!E164_RE.test(v)) return false;
  const dial = TEN_DIGIT_DIALS.find(d => v.startsWith('+' + d));
  return dial ? v.length - 1 - dial.length === 10 : true;
}

/** Teléfono sin '+' inicial (para mostrar la ayuda de código de país). */
export function phoneMissingPlus(value: string): boolean {
  const v = normalizePhoneInput(value);
  return v.length > 0 && !v.startsWith('+');
}

export interface PasswordRules {
  length: boolean;
  upper: boolean;
  lower: boolean;
  number: boolean;
  special: boolean;
}

/** Longitud mínima por defecto del backend (passwordPolicy.minLength = 8). */
export const MIN_PASSWORD_LENGTH = 8;

/** Evalúa cada regla de fortaleza de una contraseña nueva. */
export function passwordRules(value: string, minLength: number = MIN_PASSWORD_LENGTH): PasswordRules {
  return {
    length: value.length >= minLength,
    upper: /\p{Lu}/u.test(value),
    lower: /\p{Ll}/u.test(value),
    number: /\d/.test(value),
    special: /[^\p{L}\p{N}\s]/u.test(value),
  };
}

export function isStrongPassword(value: string, minLength: number = MIN_PASSWORD_LENGTH): boolean {
  return Object.values(passwordRules(value, minLength)).every(Boolean);
}

/** Contraseña nueva lista para enviar: cumple todas las reglas y coincide con la confirmación. */
export function isValidNewPassword(value: string, confirm: string, minLength: number = MIN_PASSWORD_LENGTH): boolean {
  return isStrongPassword(value, minLength) && value === confirm;
}

export type ResetMethod = 'telegram' | 'email';

/** ¿El dato es válido para el método de recuperación elegido? */
export function isValidResetTarget(method: ResetMethod, value: string): boolean {
  return method === 'email' ? isValidEmail(value) : isValidPhoneE164(value);
}
